import * as yup from "yup";
import { createAddressFields } from "../constants";
import { type FormSectionConfig, type RadioOption, type TaxFormConfig } from "../types";

export const US_STATUS_OPTIONS: RadioOption[] = [
  {
    value: "us_citizen_or_resident",
    label:
      "I confirm that I am a U.S. citizen and/or resident in the U.S. for tax purposes (green card holder or resident under the substantial presence test) and my U.S. federal taxpayer identifying number (U.S. TIN) is as follows:",
    nestedField: {
      name: "usTin",
      type: "text",
      label: "US TIN",
      placeholder: "",
      required: true,
    },
  },
  {
    value: "former_us_citizen",
    label:
      "I confirm that I was born in the U.S. (or a U.S. territory) but am no longer a U.S. citizen as I have voluntarily surrendered my citizenship as evidenced by the attached documents.",
  },
  {
    value: "not_us_citizen_or_resident",
    label: "I confirm that I am not a U.S. citizen or resident in the U.S. for tax purposes.",
  },
];

const sections: FormSectionConfig[] = [
  {
    id: "accountHolderIdentification",
    title: "Account Holder Identification",
    fields: [
      {
        name: "name",
        type: "text",
        label: "Name",
        placeholder: "",
        required: true,
        gridColumn: "half",
      },
      {
        name: "dateOfBirth",
        type: "date",
        label: "Date of birth",
        required: true,
        gridColumn: "half",
      },
      {
        name: "cityOfBirth",
        type: "text",
        label: "City of birth",
        placeholder: "",
        required: true,
        gridColumn: "half",
      },
      {
        name: "countryOfBirth",
        type: "country",
        label: "Country of birth",
        placeholder: "Select a country",
        required: true,
        gridColumn: "half",
      },
      {
        name: "_permanentAddressLabel",
        type: "label",
        label: "Permanent residence address",
        required: true,
        linkedField: "permanentAddress",
        gridColumn: "full",
      },
      ...createAddressFields("permanentAddress"),
    ],
  },
  {
    id: "taxResidency",
    title: "TAX RESIDENCY(IES)",
    actionLabel: "Add tax residency",
    fields: [
      {
        name: "taxResidencies",
        type: "taxResidency",
        label: "TAX RESIDENCY(IES)",
        required: true,
        gridColumn: "full",
      },
    ],
  },
  {
    id: "usDeclaration",
    title: "Declaration of U.S. Citizenship or U.S. Residence for Tax Purposes",
    fields: [
      {
        name: "usStatusDeclaration",
        type: "radio",
        label: "US status declaration",
        required: true,
        gridColumn: "full",
        radioOptions: US_STATUS_OPTIONS,
      },
    ],
  },
];

const validationSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  dateOfBirth: yup.date().nullable().required("Date of birth is required"),
  cityOfBirth: yup.string().required("City of birth is required"),
  countryOfBirth: yup.string().required("Country of birth is required"),
  permanentAddress: yup.object().shape({
    country: yup.string().required("Country is required"),
    streetLine1: yup.string().required("Street (line 1) is required"),
    city: yup.string().required("City is required"),
    zipCode: yup.string().required("Zip code is required"),
  }),
  taxResidencies: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        jurisdiction: yup.string().required("Jurisdiction of tax residency is required"),
        hasTaxReferenceNumber: yup.boolean().required(),
        taxReferenceNumberType: yup.string().when("hasTaxReferenceNumber", {
          is: true,
          then: (schema) => schema.required("Tax reference number type is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        taxReferenceNumber: yup.string().when("hasTaxReferenceNumber", {
          is: true,
          then: (schema) => schema.required("Tax reference number is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        reasonForNoTin: yup.string().when("hasTaxReferenceNumber", {
          is: false,
          then: (schema) => schema.required("Reason for no TIN is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    )
    .min(1, "Tax residency is required"),
  usStatusDeclaration: yup.string().required("US status declaration is required"),
  usTin: yup.string().when("usStatusDeclaration", {
    is: "us_citizen_or_resident",
    then: (schema) => schema.required("US TIN is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const initialValues = {
  name: "",
  dateOfBirth: null,
  cityOfBirth: "",
  countryOfBirth: "",
  permanentAddress: {
    country: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    stateOrProvince: "",
    zipCode: "",
  },
  taxResidencies: [],
  usStatusDeclaration: "",
  usTin: "",
};

export const crsIndividualConfig: TaxFormConfig = {
  formId: "CRS_INDIVIDUAL",
  title: "New CRS details",
  subtitle: "Individual Self Certification",
  sections,
  validationSchema,
  initialValues,
};
